import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { RecordedSave } from "@/admin/Recorded";
import { Btn, Pill, Table } from "@/admin/AdminLayout";
import { inputCls } from "@/components/common";

const blank = { from_path: "", to_path: "", code: 301 };
const day = (s?: string) => (s ? new Date(s).toLocaleDateString("en-AU") : "—");

function RedirectForm({ value, onSave, onCancel, testId, onRestored }: any) {
  const [f, setF] = useState<any>(value);
  useEffect(() => setF(value), [value]);
  return (
    <div className="grid md:grid-cols-[1fr_1fr_150px_auto] gap-3 items-center" data-testid={testId}>
      <input value={f.from_path} onChange={(e) => setF({ ...f, from_path: e.target.value })} placeholder="From: /old-page  or  /old-folder/*" className={inputCls} data-testid={`${testId}-from`} />
      <input value={f.to_path} onChange={(e) => setF({ ...f, to_path: e.target.value })} placeholder="To: /new-page  or  /new-folder/*" className={inputCls} data-testid={`${testId}-to`} />
      <select value={f.code} onChange={(e) => setF({ ...f, code: Number(e.target.value) })} className={inputCls} data-testid={`${testId}-code`}><option value={301}>301 Permanent</option><option value={302}>302 Temporary</option></select>
      <div className="flex gap-2 items-start">{value.id ? <RecordedSave res="redirects" id={value.id} onSave={() => onSave(f)} value={f} testId={`${testId}-save`} onRestored={onRestored} /> : <Btn onClick={() => onSave(f)} data-testid={`${testId}-save`}>Save</Btn>}{onCancel && <Btn ghost onClick={onCancel}>Cancel</Btn>}</div>
    </div>
  );
}

function ImportDialog({ open, onClose, onDone }: any) {
  const [text, setText] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [res, setRes] = useState<any>(null);
  const run = async () => {
    try { const r = await api.post("/admin-x/redirects/import", { csv: text, overwrite }); setRes(r.data); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const file = (f?: File) => f && f.text().then(setText);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setRes(null); onClose(); } }}>
      <DialogContent className="bg-white max-w-2xl" data-testid="redirect-import-dialog">
        <DialogTitle className="font-display text-2xl">Import redirects</DialogTitle>
        <p className="text-sm text-neutral-600">One per line: <code>from,to</code> or <code>from,to,302</code>. A heading row is fine. Export from your old site or a spreadsheet as CSV.</p>
        <input type="file" accept=".csv,text/csv,text/plain" onChange={(e) => file(e.target.files?.[0])} className="text-sm" data-testid="redirect-import-file" />
        <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder={"/old-rings,/collections/engagement-rings\n/journal/*,/blog/*"} className={inputCls + " font-mono text-xs"} data-testid="redirect-import-text" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="redirect-import-overwrite" />Replace existing redirects with the same 'from' address</label>
        <Btn onClick={run} disabled={!text.trim()} data-testid="redirect-import-run">Import</Btn>
        {res && <div className="text-sm space-y-1" data-testid="redirect-import-result"><p>Added {res.created} · Updated {res.updated} · Skipped {res.errors.length}</p>
          {res.errors.map((e: any) => <p key={e.line} className="text-xs text-red-700">Line {e.line} ({e.from}): {e.reason}</p>)}</div>}
      </DialogContent>
    </Dialog>
  );
}

export function RedirectsAdmin({ prefillFrom = "" }: { prefillFrom?: string }) {
  const [items, setItems] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [draft, setDraft] = useState({ ...blank, from_path: prefillFrom });
  const load = () => api.get("/admin-x/redirects").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async (f: any, id?: string) => {
    try { if (id) await api.put(`/admin-x/redirects/${id}`, f); else await api.post("/admin-x/redirects", f); toast("Redirect saved"); if (!id) setDraft({ ...blank }); load(); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  const del = async (id: string) => { if (window.confirm("Delete this redirect?")) { await api.delete(`/admin-x/redirects/${id}`); load(); } };
  const exportCsv = async () => {
    const r = await api.get("/admin-x/redirects/export", { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "redirects.csv"; a.click();
  };
  const shown = items.filter((r) => !q || `${r.from_path} ${r.to_path}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-6" data-testid="redirects-admin">
      <section className="bg-white border border-neutral-200 p-6 space-y-3">
        <p className="eyebrow text-[10px] text-neutral-500">Add a redirect</p>
        <RedirectForm value={draft} onSave={(f: any) => save(f)} testId="redirect-new" />
        <p className="text-[11px] text-neutral-500">End the 'from' address with /* to move a whole folder, e.g. /journal/* → /blog/* sends /journal/oval-guide to /blog/oval-guide. Product address changes from the nightly sync are added automatically. Chains are followed, so visitors always land on the final page.</p>
      </section>
      <div className="flex flex-wrap gap-3 items-center">
        <input placeholder="Search redirects…" value={q} onChange={(e) => setQ(e.target.value)} className="border border-neutral-300 bg-white px-3 py-2 text-sm" data-testid="redirect-search" />
        <span className="text-xs text-neutral-500">{items.length} redirects</span>
        <div className="ml-auto flex gap-2"><Btn ghost onClick={() => setImporting(true)} data-testid="redirect-import-open">Import CSV</Btn><Btn ghost onClick={exportCsv} data-testid="redirect-export">Export CSV</Btn></div>
      </div>
      <Table head={["From", "To", "Type", "Used", "Last used", ""]} testId="redirects-table">
        {shown.map((r) => editing === r.id ? (
          <tr key={r.id}><td colSpan={6} className="px-4 py-3"><RedirectForm value={r} onSave={(f: any) => save(f, r.id)} onCancel={() => setEditing(null)} onRestored={() => { setEditing(null); load(); }} testId="redirect-edit" /></td></tr>
        ) : (
          <tr key={r.id} data-testid={`redirect-row-${r.id}`}>
            <td className="px-4 py-3 font-mono text-xs break-all">{r.from_path}</td>
            <td className="px-4 py-3 font-mono text-xs break-all">{r.to_path}</td>
            <td className="px-4 py-3"><Pill tone={r.code === 302 ? "amber" : "neutral"}>{r.code === 302 ? "302 temporary" : "301 permanent"}</Pill></td>
            <td className="px-4 py-3 text-xs">{r.hits || 0}×</td>
            <td className="px-4 py-3 text-xs">{day(r.last_hit_at)}</td>
            <td className="px-4 py-3 text-right whitespace-nowrap space-x-2"><Btn ghost onClick={() => setEditing(r.id)} data-testid={`redirect-edit-${r.id}`}>Edit</Btn><Btn ghost onClick={() => del(r.id)} data-testid={`redirect-delete-${r.id}`}>Delete</Btn></td>
          </tr>
        ))}
      </Table>
      <ImportDialog open={importing} onClose={() => setImporting(false)} onDone={load} />
    </div>
  );
}
